package com.xorrus.trading.lot.relayclient.browser

import com.xorrus.trading.lot.relay.command.PushEvent
import com.xorrus.trading.lot.relay.crypto.Base64Url
import com.xorrus.trading.lot.relay.crypto.WebCryptoProvider
import com.xorrus.trading.lot.relay.engine.ControllerEngineConfig
import com.xorrus.trading.lot.relay.engine.ControllerRelayEngine
import com.xorrus.trading.lot.relay.engine.KtorRelayConnector
import com.xorrus.trading.lot.relay.engine.ManagedControllerClient
import com.xorrus.trading.lot.relay.engine.RequestOutcome
import com.xorrus.trading.lot.relay.ports.StoredPairing
import com.xorrus.trading.lot.relay.protocol.ProtocolCrypto
import com.xorrus.trading.lot.relay.storage.IndexedDbKeyStorage
import com.xorrus.trading.lot.relayclient.facade.LotController
import com.xorrus.trading.lot.relayclient.facade.RelayRequester
import com.xorrus.trading.lot.relayclient.model.DesktopStatus
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.JsonObject

/**
 * Controller-Seite der Remote-SPAs über der generischen Relay-Lib: EINE persistente
 * Geräte-Identität in IndexedDB, beliebig viele Pairings (je Desktop eines, Lib ≥ 0.5.0)
 * und je gekoppeltem Desktop ein [Desktop]-Bündel aus Session-Client mit transparentem
 * Rekey-Reconnect ([ManagedControllerClient]) und typisierter LOT-Fassade ([LotController]).
 *
 * Genau ein Desktop ist der **aktuelle** ([selected]/[select]): die Strategie-SPA arbeitet
 * ausschließlich mit ihm ([lot]); die Auswahl ist zugleich der Legacy-Slot der Lib, sodass
 * ältere Strategie-SPA-Builds denselben Desktop sehen.
 *
 * Geteilt zwischen connect-SPA (`remote-connect`: Pairing, Desktop-Liste, Channel-Weiterleitung)
 * und Strategie-SPA (`remote-strategy`: Steuerung) — beide laufen auf demselben Origin
 * (remote.lab-of-trades.com), sehen also dieselbe IndexedDB-Identität.
 */
object ControllerService {

    val scope = CoroutineScope(SupervisorJob())

    private val provider = WebCryptoProvider()
    private val pc = ProtocolCrypto(provider)
    private val storage = IndexedDbKeyStorage(provider)

    private val engine = ControllerRelayEngine(
        pc = pc,
        connector = KtorRelayConnector(scope),
        keyStorage = storage,
        config = ControllerEngineConfig(
            label = deviceLabel(),
            ua = window.navigator.userAgent,
        ),
        scope = scope,
    )

    /** Ein gekoppelter Desktop: Pairing-Daten, Session-Client und LOT-Fassade. [id] ist die URL-Kennung. */
    class Desktop internal constructor(
        val pairing: StoredPairing,
        internal val client: ManagedControllerClient,
    ) {
        val hostId: ByteArray get() = pairing.hostId
        val id: String = Base64Url.encode(pairing.hostId)

        /** Typisierte LOT-Fassade dieses Desktops — die einzige Kommando-Schnittstelle. */
        val lot: LotController = LotController(object : RelayRequester {
            override val events: Flow<PushEvent> get() = client.events
            override suspend fun request(cmd: String, args: JsonObject): RequestOutcome = client.request(cmd, args)
        })

        /** Grund, aus dem der Client dauerhaft beendet ist (`revoked` = am Desktop entfernt); null = nutzbar. */
        val terminated: String? get() = client.terminatedReason

        val info: DesktopInfo get() = DesktopDirectory.get(id)
        val displayName: String get() = DesktopDirectory.displayName(id)
    }

    private val registry = LinkedHashMap<String, Desktop>()
    private val mutex = Mutex()
    private var current: Desktop? = null

    /** Alle gekoppelten Desktops (Bündel werden lazy aus dem Storage aufgebaut). */
    suspend fun desktops(): List<Desktop> = mutex.withLock {
        syncLocked()
        registry.values.toList()
    }

    suspend fun desktop(id: String): Desktop? = mutex.withLock {
        syncLocked()
        registry[id]
    }

    /** Der aktuell ausgewählte Desktop (Legacy-Slot der Lib); null = keiner gekoppelt/gewählt. */
    suspend fun selected(): Desktop? = mutex.withLock {
        syncLocked()
        val stored = storage.loadPairing() ?: return null
        registry[Base64Url.encode(stored.hostId)].also { current = it }
    }

    /** Macht [id] zum aktuellen Desktop (persistent); null = unbekannte Kennung. */
    suspend fun select(id: String): Desktop? = mutex.withLock {
        syncLocked()
        val desktop = registry[id] ?: return null
        storage.selectPairing(desktop.hostId)
        current = desktop
        desktop
    }

    /**
     * Koppelt gegen den gescannten `lotpair://`-QR-Inhalt. [onSas] MUSS den
     * Vergleichscode anzeigen, den der Nutzer am Desktop bestätigt (Protokoll-§7.2).
     * Der neue Desktop wird der aktuelle. Ein vorhandenes Pairing desselben Desktops
     * (gleiche Identität `D_id`, z. B. nach hostId-Rotation) wird dabei ersetzt.
     */
    suspend fun pair(qrUri: String, onSas: (String) -> Unit): Desktop {
        val stored = engine.pair(qrUri) { onSas(it) }
        return mutex.withLock {
            val id = Base64Url.encode(stored.hostId)
            registry.values
                .filter { it.id != id && it.pairing.dIdPub.contentEquals(stored.dIdPub) }
                .forEach { stale ->
                    stale.client.closeQuietly()
                    registry.remove(stale.id)
                    storage.removePairing(stale.hostId)
                    DesktopDirectory.move(stale.id, id)
                }
            // erneut gekoppelt: neuer lookupKey — altes Bündel ist wertlos
            registry.remove(id)?.client?.closeQuietly()
            bundle(stored).also {
                registry[id] = it
                current = it
            }
        }
    }

    /**
     * Entkoppelt GENAU diesen Desktop lokal: Session schließen, Pairing entfernen; war es
     * das letzte, wird der komplette Speicher inklusive Geräte-Identität gelöscht
     * ([IndexedDbKeyStorage.wipe]). Der Desktop behält seinen Allowlist-Eintrag — dort über
     * Einstellungen → Remote Pairing entfernen. Die Navigation danach macht der Aufrufer.
     */
    suspend fun unpair(id: String): Unit = mutex.withLock {
        syncLocked()
        val desktop = registry.remove(id) ?: return
        desktop.client.closeQuietly()
        DesktopDirectory.forget(id)
        try {
            if (registry.isEmpty()) {
                storage.wipe()
            } else {
                storage.removePairing(desktop.hostId)
                if (storage.loadPairing() == null) storage.selectPairing(registry.values.first().hostId)
            }
        } catch (e: Exception) {
            console.error("Entkoppeln: Storage-Änderung fehlgeschlagen", e)
        }
        if (current?.id == id) current = null
    }

    /** Channel/Version aus einer `desktop.status`-Antwort für die Offline-Liste merken. */
    fun rememberStatus(id: String, status: DesktopStatus) = DesktopDirectory.remember(id, status)

    /** Anzeigename eines Desktops (Platzhalter, solange keiner vergeben ist) — auch ohne Bündel nutzbar. */
    fun desktopDisplayName(id: String): String = DesktopDirectory.displayName(id)

    /** Der vom Nutzer vergebene Name; null = (noch) keiner. */
    fun desktopName(id: String): String? = DesktopDirectory.get(id).name

    /**
     * Benennt einen Desktop (leer = Name entfernen). Der Name lebt nur in diesem Browser —
     * der Desktop erfährt ihn nicht.
     */
    fun renameDesktop(id: String, name: String) = DesktopDirectory.rename(id, name)

    /** Fassade des aktuellen Desktops — Ein-Desktop-Sicht der Strategie-SPA. */
    val lot: LotController
        get() = current?.lot ?: error("kein Desktop ausgewählt")

    /** Baut die Session des aktuellen Desktops auf (Resume-Handshake); Reconnect übernimmt der Client. */
    suspend fun connect() {
        (current ?: selected())?.client?.connect()
    }

    // ---- intern ------------------------------------------------------------------

    /** Registry mit dem Storage abgleichen: neue/ersetzte Pairings bündeln, verwaiste Bündel schließen. */
    private suspend fun syncLocked() {
        val stored = storage.loadPairings()
        val ids = stored.map { Base64Url.encode(it.hostId) }.toSet()
        registry.keys.filter { it !in ids }.toList().forEach { id ->
            registry.remove(id)?.client?.closeQuietly()
            if (current?.id == id) current = null
        }
        stored.forEach { pairing ->
            val id = Base64Url.encode(pairing.hostId)
            val existing = registry[id]
            if (existing == null || !existing.pairing.lookupKey.contentEquals(pairing.lookupKey)) {
                existing?.client?.closeQuietly()
                registry[id] = bundle(pairing)
                if (current?.id == id) current = registry[id]
            }
        }
    }

    private fun bundle(pairing: StoredPairing): Desktop =
        Desktop(pairing, ManagedControllerClient(engine, scope, hostId = pairing.hostId))

    private suspend fun ManagedControllerClient.closeQuietly() {
        try {
            close()
        } catch (_: Exception) {
        }
    }

    private fun deviceLabel(): String {
        val ua = window.navigator.userAgent
        val device = when {
            "iPhone" in ua -> "iPhone"
            "iPad" in ua -> "iPad"
            "Android" in ua -> "Android"
            else -> "Browser"
        }
        return "LOT Remote Control ($device)"
    }
}
